import { useCallback, useEffect } from 'react';

import { useAuthStore } from '../store/auth';
import { authApi } from '../api/auth';
import api from '../lib/axios';
import { clearProductsStatusNotificationModalSessionFlag } from '../utils/productsStatusNotificationSession';

export const useAuth = () => {
  const authStore = useAuthStore();

  const login = async (email, password) => {
    try {
      const result = await authStore.login(email, password);

      // Se 2FA for requerido, não tenta carregar dados nem setar token agora
      if (result?.requiresTwoFactor) {
        return { requiresTwoFactor: true, tempToken: result.tempToken, message: result.message };
      }

      // Email não verificado
      if (result?.emailNotVerified) {
        return { emailNotVerified: true, error: result.error };
      }

      // Se login falhou, propaga erro padronizado
      if (!result?.success) {
        return {
          success: false,
          error: result?.error || 'Credenciais inválidas',
          status: result?.status,
        };
      }

      // Sucesso: guardar usuário do login (permite entrar mesmo se /me falhar)
      authStore.setUser(result.user);

      // Tentar carregar dados completos (role). Se /me falhar (ex.: 500), o usuário já está definido pelo login.
      try {
        await loadUserData();
      } catch (_err) {
        // Não falha o login: usuário já foi setado acima; permissões podem ficar limitadas até o backend retornar role em login ou corrigir /me
      }

      return { success: true, access_token: result.access_token, user: result.user };
    } catch (error) {
      throw error;
    }
  };

  const logout = () => {
    authStore.logout();
    window.location.href = '/login';
  };

  const loadUserData = useCallback(async () => {
    try {
      const response = await authApi.me();
      // A resposta tem a estrutura { user: { ... } }
      const userData = response.data.user || response.data;

      // Atualizar dados do usuário
      authStore.setUser(userData);

      return userData;
    } catch (error) {
      throw error;
    }
  }, [authStore]);

  const refreshUserData = async () => {
    if (authStore.isAuthenticated()) {
      try {
        await loadUserData();
      } catch (error) {
        // Se falhar ao recarregar dados, fazer logout
        logout();
      }
    }
  };

  const verifyTwoFactor = async (code, tempToken) => {
    try {
      const response = await api.post(
        '/auth/2fa/verify',
        { code, temp_token: tempToken },
        { timeout: 25000 },
      );

      const { access_token, user: userData } = response.data;
      clearProductsStatusNotificationModalSessionFlag();
      authStore.setToken(access_token);
      authStore.setUser(userData);

      // Carregar dados completos do usuário após 2FA
      await loadUserData();

      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Código inválido',
      };
    }
  };

  const resendVerificationEmail = async email => {
    try {
      await api.post('/auth/email/resend', { email });
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Erro ao reenviar e-mail',
      };
    }
  };

  // Carregar dados do usuário automaticamente se houver token
  useEffect(() => {
    if (authStore.token && !authStore.user) {
      loadUserData().catch(() => {
        // Se falhar, limpar token
        authStore.clearToken();
      });
    }
  }, [authStore.token, authStore.user, authStore, loadUserData]);

  return {
    user: authStore.user,
    token: authStore.token,
    isAuthenticated: authStore.isAuthenticated(),
    login,
    logout,
    loadUserData,
    refreshUserData,
    verifyTwoFactor,
    resendVerificationEmail,
    loading: !authStore.token && !authStore.user, // Loading inicial
  };
};
